import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaArrowUp,
  FaArrowDown,
  FaRegEye,
  FaRegEyeSlash,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const SLUG_OPTIONS = [
  { value: 'startup-accounting-tax-compliance', label: 'Startup Accounting & Tax Compliance' },
  { value: 'ecommerce-accounting-tax-services', label: 'E-Commerce Accounting & Tax Services' }
];

const COMMON_ICONS = [
  'Building2', 'FileText', 'ShieldCheck', 'PieChart', 'Rocket', 'Sprout', 'TrendingUp',
  'Globe', 'Clock', 'MessageSquare', 'Cloud', 'Lightbulb', 'Zap', 'Laptop', 'Layers',
  'UserCheck', 'BarChart', 'FileCheck', 'Users', 'Target', 'Network', 'PiggyBank',
  'FileSignature', 'LayoutDashboard', 'CreditCard', 'PackageOpen', 'Briefcase',
  'ShoppingCart', 'Package', 'CheckCircle2'
];

const IndustryPagesCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeSlug, setActiveSlug] = useState('startup-accounting-tax-compliance');
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  
  const [form, setForm] = useState({
    heroTitle: '',
    heroBody: '',
    heroBullets: [],
    heroButtonText: '',
    heroDescriptionHtml: '',
    heroImage: '',
    servicesTitle: '',
    services: [],
    platformsTitle: '',
    platformsSubtitle: '',
    platforms: [],
    platformsFootText: '',
    stagesTitle: '',
    stagesSubtitle: '',
    stages: [],
    stagesFootText: '',
    featuresTitle: '',
    featuresSubtitle: '',
    features: [],
    featuresFootText: '',
    whoWeWorkWithTitle: '',
    whoWeWorkWithSubtitle: '',
    whoWeWorkWithItems: [],
    whoWeWorkWithFootText: '',
    whyChooseTitle: '',
    whyChooseSubtitle: '',
    whyChooseItems: [],
    whyChooseFootText: '',
    caseStudyTitle: '',
    caseStudySubtitle: '',
    caseStudyItems: [],
    caseStudyFootText: '',
    pricingTitle: '',
    pricingSubtitle: '',
    pricingItems: [],
    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],
    isPublished: true
  });

  const fetchContent = async (slug) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/industry/admin/${slug}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data || {};
        setForm({
          heroTitle: data.heroTitle || '',
          heroBody: data.heroBody || '',
          heroBullets: data.heroBullets || [],
          heroButtonText: data.heroButtonText || '',
          heroDescriptionHtml: data.heroDescriptionHtml || '',
          heroImage: data.heroImage || '',
          servicesTitle: data.servicesTitle || '',
          services: data.services || [],
          platformsTitle: data.platformsTitle || '',
          platformsSubtitle: data.platformsSubtitle || '',
          platforms: data.platforms || [],
          platformsFootText: data.platformsFootText || '',
          stagesTitle: data.stagesTitle || '',
          stagesSubtitle: data.stagesSubtitle || '',
          stages: data.stages || [],
          stagesFootText: data.stagesFootText || '',
          featuresTitle: data.featuresTitle || '',
          featuresSubtitle: data.featuresSubtitle || '',
          features: data.features || [],
          featuresFootText: data.featuresFootText || '',
          whoWeWorkWithTitle: data.whoWeWorkWithTitle || '',
          whoWeWorkWithSubtitle: data.whoWeWorkWithSubtitle || '',
          whoWeWorkWithItems: data.whoWeWorkWithItems || [],
          whoWeWorkWithFootText: data.whoWeWorkWithFootText || '',
          whyChooseTitle: data.whyChooseTitle || '',
          whyChooseSubtitle: data.whyChooseSubtitle || '',
          whyChooseItems: data.whyChooseItems || [],
          whyChooseFootText: data.whyChooseFootText || '',
          caseStudyTitle: data.caseStudyTitle || '',
          caseStudySubtitle: data.caseStudySubtitle || '',
          caseStudyItems: data.caseStudyItems || [],
          caseStudyFootText: data.caseStudyFootText || '',
          pricingTitle: data.pricingTitle || '',
          pricingSubtitle: data.pricingSubtitle || '',
          pricingItems: data.pricingItems || [],
          faqsTitle: data.faqsTitle || '',
          faqsSubtitle: data.faqsSubtitle || '',
          faqs: data.faqs || [],
          isPublished: data.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent(activeSlug);
  }, [activeSlug]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/industry/admin/${activeSlug}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Content saved successfully');
        fetchContent(activeSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert this page to the default original layout? All local edits on this page will be lost.')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/industry/admin/${activeSlug}/reload-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent(activeSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingHeroImage(true);
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/industry/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (res.data.success) {
        updateField('heroImage', res.data.url);
        toastSuccess('Hero image uploaded successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload hero image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  // Helper lists operations
  const addBullet = (field) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const updateBullet = (field, index, val) => {
    setForm(prev => {
      const list = [...prev[field]];
      list[index] = val;
      return { ...prev, [field]: list };
    });
  };

  const removeBullet = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const moveBullet = (field, index, dir) => {
    const nextIndex = index + dir;
    if (nextIndex < 0 || nextIndex >= form[field].length) return;
    setForm(prev => {
      const list = [...prev[field]];
      [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
      return { ...prev, [field]: list };
    });
  };

  // Object lists operations
  const addObjectItem = (field, initialObj) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], initialObj] }));
  };

  const updateObjectItem = (field, index, key, val) => {
    setForm(prev => {
      const list = [...prev[field]];
      list[index] = { ...list[index], [key]: val };
      return { ...prev, [field]: list };
    });
  };

  const removeObjectItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const moveObjectItem = (field, index, dir) => {
    const nextIndex = index + dir;
    if (nextIndex < 0 || nextIndex >= form[field].length) return;
    setForm(prev => {
      const list = [...prev[field]];
      [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
      return { ...prev, [field]: list };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  const isEcommerce = activeSlug === 'ecommerce-accounting-tax-services';

  return (
    <PageLayout>
      <PageHeader
        title="Industry Landing Pages CMS"
        description="Configure dynamic content for Startup and E-Commerce Accounting landing pages"
        icon={FaGlobe}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Switcher & Tab navigation */}
      <div className="flex flex-col md:flex-row gap-5 items-stretch md:items-center justify-between mb-6">
        <div className="w-full md:w-80">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1">Select Page</label>
          <select
            className="input-modern w-full"
            value={activeSlug}
            onChange={(e) => {
              setActiveSlug(e.target.value);
              setActiveTab('hero');
            }}
          >
            {SLUG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex bg-gray-100 dark:bg-dark-850 p-1.5 rounded-xl self-end">
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => updateField('isPublished', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Page Published
          </label>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6">
        {[
          { id: 'hero', name: 'Hero / General' },
          { id: 'services', name: 'Services' },
          ...(isEcommerce ? [{ id: 'platforms', name: 'Platforms' }] : []),
          { id: 'stages', name: isEcommerce ? 'Scale Steps' : 'Growth Stages' },
          ...(!isEcommerce ? [{ id: 'features', name: 'We Speak Startup' }] : []),
          { id: 'whoWeWorkWith', name: 'Who We Work With' },
          { id: 'whyChoose', name: 'Why Choose' },
          { id: 'caseStudy', name: 'Case Study & Results' },
          { id: 'pricing', name: 'Pricing' },
          { id: 'faqs', name: 'FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* TAB 1: Hero & General */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Hero Section</h3>
            
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title</label>
              <input
                className="input-modern"
                value={form.heroTitle}
                onChange={(e) => updateField('heroTitle', e.target.value)}
                placeholder="Title of the landing page"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Subtitle Body</label>
              <textarea
                className="input-modern min-h-[80px]"
                value={form.heroBody}
                onChange={(e) => updateField('heroBody', e.target.value)}
                placeholder="Introductory subtitle paragraph"
              />
            </div>

            {/* Bullets array */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Bullets (Checkmarks)</label>
                <button
                  onClick={() => addBullet('heroBullets')}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 font-bold"
                >
                  <FaPlus /> Add Bullet
                </button>
              </div>
              <div className="space-y-2">
                {form.heroBullets.map((bullet, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      className="input-modern flex-1"
                      value={bullet}
                      onChange={(e) => updateBullet('heroBullets', idx, e.target.value)}
                      placeholder="Enter bullet point text"
                    />
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => moveBullet('heroBullets', idx, -1)}
                      className="p-2 border rounded-xl hover:bg-gray-50 disabled:opacity-40"
                    >
                      <FaArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === form.heroBullets.length - 1}
                      onClick={() => moveBullet('heroBullets', idx, 1)}
                      className="p-2 border rounded-xl hover:bg-gray-50 disabled:opacity-40"
                    >
                      <FaArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeBullet('heroBullets', idx)}
                      className="p-2 text-red-500 border rounded-xl hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">CTA Button Text</label>
                <input
                  className="input-modern"
                  value={form.heroButtonText}
                  onChange={(e) => updateField('heroButtonText', e.target.value)}
                  placeholder="👉 Book a Free Strategy Call Today"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Sub-CTA Description Link (HTML allowed)</label>
                <input
                  className="input-modern"
                  value={form.heroDescriptionHtml}
                  onChange={(e) => updateField('heroDescriptionHtml', e.target.value)}
                  placeholder="Intro link or info description HTML"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block">Hero Image</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                {form.heroImage ? (
                  <div className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-750 bg-gray-50 dark:bg-dark-900 max-h-[160px] flex items-center justify-center">
                    <img
                      src={
                        form.heroImage.startsWith('http') || form.heroImage.startsWith('/images')
                          ? form.heroImage
                          : `${(process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/api$/, '')}/${form.heroImage.startsWith('/') ? form.heroImage.substring(1) : form.heroImage}`
                      }
                      alt="Hero preview"
                      className="w-full h-auto max-h-[160px] object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-2xl p-6 text-center text-gray-500 h-[160px]">
                    <FaUpload className="w-6 h-6 mb-2 text-gray-400" />
                    <p className="text-xs font-semibold">No Image Selected</p>
                  </div>
                )}
                
                <div className="md:col-span-2 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      id="hero-image-upload"
                      accept="image/*"
                      onChange={handleHeroImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="hero-image-upload"
                      className={`btn-premium flex items-center justify-center gap-2 cursor-pointer text-center py-2 px-4 text-sm font-semibold rounded-xl ${uploadingHeroImage ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <FaUpload />
                      {uploadingHeroImage ? 'Uploading...' : 'Upload Image File'}
                    </label>
                    {form.heroImage && (
                      <button
                        type="button"
                        onClick={() => updateField('heroImage', '')}
                        className="px-4 py-2 border border-red-200 dark:border-red-900/30 text-red-500 rounded-xl text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        Clear Image
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] text-gray-400 block font-medium">Or enter a custom Image URL directly:</span>
                    <input
                      className="input-modern w-full"
                      value={form.heroImage}
                      onChange={(e) => updateField('heroImage', e.target.value)}
                      placeholder="https://images.unsplash.com/... or uploads/..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Services */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Services Section</h3>
              <button
                onClick={() => addObjectItem('services', { title: '', description: '', icon: 'Building2', bullets: [], footText: '', isBigCard: false, supportedTechs: [] })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Service Card
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Services Section Title</label>
              <input
                className="input-modern"
                value={form.servicesTitle}
                onChange={(e) => updateField('servicesTitle', e.target.value)}
                placeholder="Our Startup Accounting Services"
              />
            </div>

            <div className="space-y-6 mt-4">
              {form.services.map((service, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-5 space-y-4 bg-gray-50/50 dark:bg-dark-900/40 relative">
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-dark-750 pb-2">
                    <span className="font-bold text-sm text-primary-600">Service Card #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('services', idx, -1)}
                        className="p-1.5 border rounded-lg hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.services.length - 1}
                        onClick={() => moveObjectItem('services', idx, 1)}
                        className="p-1.5 border rounded-lg hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('services', idx)}
                        className="p-1.5 text-red-500 border rounded-lg hover:bg-red-50 text-xs"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-xs font-semibold text-gray-500">Card Title</label>
                      <input
                        className="input-modern"
                        value={service.title}
                        onChange={(e) => updateObjectItem('services', idx, 'title', e.target.value)}
                        placeholder="e.g. 2.1 Entity Formation"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500">Icon Component</label>
                      <select
                        className="input-modern"
                        value={service.icon}
                        onChange={(e) => updateObjectItem('services', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Description</label>
                    <textarea
                      className="input-modern min-h-[60px]"
                      value={service.description}
                      onChange={(e) => updateObjectItem('services', idx, 'description', e.target.value)}
                      placeholder="Intro explanation for service"
                    />
                  </div>

                  {/* Bullet points for service */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-500">Service Features / Deliverables</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newBullets = [...(service.bullets || []), ''];
                          updateObjectItem('services', idx, 'bullets', newBullets);
                        }}
                        className="text-xs text-primary-600 flex items-center gap-1 font-bold"
                      >
                        <FaPlus /> Add Bullet
                      </button>
                    </div>
                    {(service.bullets || []).map((b, bIdx) => (
                      <div key={bIdx} className="flex gap-2 items-center">
                        <input
                          className="input-modern flex-1"
                          value={b}
                          onChange={(e) => {
                            const newBullets = [...service.bullets];
                            newBullets[bIdx] = e.target.value;
                            updateObjectItem('services', idx, 'bullets', newBullets);
                          }}
                          placeholder="Feature description"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newBullets = service.bullets.filter((_, bi) => bi !== bIdx);
                            updateObjectItem('services', idx, 'bullets', newBullets);
                          }}
                          className="p-2 text-red-500 hover:text-red-650"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Foot Note / Link Paragraph (HTML allowed)</label>
                    <input
                      className="input-modern"
                      value={service.footText}
                      onChange={(e) => updateObjectItem('services', idx, 'footText', e.target.value)}
                      placeholder="Whether you are launching... our Outsource bookkeeping..."
                    />
                  </div>

                  <div className="border-t border-gray-200 dark:border-dark-750 pt-3 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!service.isBigCard}
                        onChange={(e) => updateObjectItem('services', idx, 'isBigCard', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Is Big Block Layout (spanning full width)
                    </label>

                    {service.isBigCard && (
                      <div className="flex-1 w-full space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-semibold text-gray-500">Supported Techs / Tags</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newTechs = [...(service.supportedTechs || []), ''];
                              updateObjectItem('services', idx, 'supportedTechs', newTechs);
                            }}
                            className="text-xs text-primary-600 flex items-center gap-1 font-bold"
                          >
                            <FaPlus /> Add Tag
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {(service.supportedTechs || []).map((tech, tIdx) => (
                            <div key={tIdx} className="flex gap-2 items-center">
                              <input
                                className="input-modern flex-1"
                                value={tech}
                                onChange={(e) => {
                                  const newTechs = [...service.supportedTechs];
                                  newTechs[tIdx] = e.target.value;
                                  updateObjectItem('services', idx, 'supportedTechs', newTechs);
                                }}
                                placeholder="QuickBooks"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newTechs = service.supportedTechs.filter((_, ti) => ti !== tIdx);
                                  updateObjectItem('services', idx, 'supportedTechs', newTechs);
                                }}
                                className="p-2 text-red-500"
                              >
                                <FaTrash className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 3: Platforms (Ecommerce specific) */}
        {activeTab === 'platforms' && isEcommerce && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Platforms We Support</h3>
              <button
                onClick={() => addObjectItem('platforms', { title: '', icon: 'ShoppingCart' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Platform
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Platforms Section Title</label>
              <input
                className="input-modern"
                value={form.platformsTitle}
                onChange={(e) => updateField('platformsTitle', e.target.value)}
                placeholder="Platforms We Support"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle (HTML allowed)</label>
              <textarea
                className="input-modern min-h-[60px]"
                value={form.platformsSubtitle}
                onChange={(e) => updateField('platformsSubtitle', e.target.value)}
                placeholder="We specialize in multi-channel ecommerce bookkeeping..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {form.platforms.map((platform, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Platform #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('platforms', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.platforms.length - 1}
                        onClick={() => moveObjectItem('platforms', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('platforms', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Platform Title</label>
                      <input
                        className="input-modern py-1"
                        value={platform.title}
                        onChange={(e) => updateObjectItem('platforms', idx, 'title', e.target.value)}
                        placeholder="Shopify / DTC"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Icon Component</label>
                      <select
                        className="input-modern py-1"
                        value={platform.icon}
                        onChange={(e) => updateObjectItem('platforms', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Platforms Section Footnote</label>
              <input
                className="input-modern"
                value={form.platformsFootText}
                onChange={(e) => updateField('platformsFootText', e.target.value)}
                placeholder="Our expertise ensures clean reconciliation..."
              />
            </div>
          </div>
        )}

        {/* TAB 4: Stages / Growth / Scale */}
        {activeTab === 'stages' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{isEcommerce ? 'Scale Steps Section' : 'Growth Stages Section'}</h3>
              <button
                onClick={() => addObjectItem('stages', { title: '', icon: 'BarChart', description: '', items: [], footText: '' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Stage Block
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Stages Section Title</label>
                <input
                  className="input-modern"
                  value={form.stagesTitle}
                  onChange={(e) => updateField('stagesTitle', e.target.value)}
                  placeholder="Supporting Startup Growth Stages"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Stages Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.stagesSubtitle}
                  onChange={(e) => updateField('stagesSubtitle', e.target.value)}
                  placeholder="As your ecommerce brand grows..."
                />
              </div>
            </div>

            <div className="space-y-6 mt-4">
              {form.stages.map((stage, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-5 space-y-4 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-dark-750 pb-2">
                    <span className="font-bold text-sm text-primary-600">Stage Block #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('stages', idx, -1)}
                        className="p-1.5 border rounded-lg hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.stages.length - 1}
                        onClick={() => moveObjectItem('stages', idx, 1)}
                        className="p-1.5 border rounded-lg hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('stages', idx)}
                        className="p-1.5 text-red-500 border rounded-lg hover:bg-red-50 text-xs"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500">Stage Title</label>
                      <input
                        className="input-modern"
                        value={stage.title}
                        onChange={(e) => updateObjectItem('stages', idx, 'title', e.target.value)}
                        placeholder="e.g. Growth & Funding Stage"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500">Icon Component</label>
                      <select
                        className="input-modern"
                        value={stage.icon}
                        onChange={(e) => updateObjectItem('stages', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Intro Description</label>
                    <textarea
                      className="input-modern min-h-[60px]"
                      value={stage.description}
                      onChange={(e) => updateObjectItem('stages', idx, 'description', e.target.value)}
                      placeholder="Intro copy describing this stage/step"
                    />
                  </div>

                  {/* Stage Bullet Items */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-500">Deliverables / Focus Items</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newItems = [...(stage.items || []), ''];
                          updateObjectItem('stages', idx, 'items', newItems);
                        }}
                        className="text-xs text-primary-600 flex items-center gap-1 font-bold"
                      >
                        <FaPlus /> Add Focus Item
                      </button>
                    </div>
                    {(stage.items || []).map((it, itIdx) => (
                      <div key={itIdx} className="flex gap-2 items-center">
                        <input
                          className="input-modern flex-1"
                          value={it}
                          onChange={(e) => {
                            const newItems = [...stage.items];
                            newItems[itIdx] = e.target.value;
                            updateObjectItem('stages', idx, 'items', newItems);
                          }}
                          placeholder="Focus item text"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newItems = stage.items.filter((_, ii) => ii !== itIdx);
                            updateObjectItem('stages', idx, 'items', newItems);
                          }}
                          className="p-2 text-red-500 hover:text-red-650"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Stage Summary / Footnote</label>
                    <input
                      className="input-modern"
                      value={stage.footText}
                      onChange={(e) => updateObjectItem('stages', idx, 'footText', e.target.value)}
                      placeholder="Outsourced accounting for funded..."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Stages Section Footnote</label>
              <input
                className="input-modern"
                value={form.stagesFootText}
                onChange={(e) => updateField('stagesFootText', e.target.value)}
                placeholder="Our advanced e-commerce financial reporting guarantees..."
              />
            </div>
          </div>
        )}

        {/* TAB 5: We Speak Startup (Startup Specific) */}
        {activeTab === 'features' && !isEcommerce && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">We Speak Startup Section</h3>
              <button
                onClick={() => addObjectItem('features', { title: '', description: '', icon: 'Clock' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Feature Block
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.featuresTitle}
                  onChange={(e) => updateField('featuresTitle', e.target.value)}
                  placeholder="We Speak Startup"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle (HTML allowed)</label>
                <input
                  className="input-modern"
                  value={form.featuresSubtitle}
                  onChange={(e) => updateField('featuresSubtitle', e.target.value)}
                  placeholder="We comprehend founders... acting as your finance partner..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {form.features.map((feat, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Feature Card #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('features', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.features.length - 1}
                        onClick={() => moveObjectItem('features', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('features', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern py-1"
                        value={feat.title}
                        onChange={(e) => updateObjectItem('features', idx, 'title', e.target.value)}
                        placeholder="Fast response times"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Icon Component</label>
                      <select
                        className="input-modern py-1"
                        value={feat.icon}
                        onChange={(e) => updateObjectItem('features', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400">Description</label>
                    <textarea
                      className="input-modern py-1 min-h-[40px]"
                      value={feat.description}
                      onChange={(e) => updateObjectItem('features', idx, 'description', e.target.value)}
                      placeholder="Short feature benefit description"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Footnote</label>
              <input
                className="input-modern"
                value={form.featuresFootText}
                onChange={(e) => updateField('featuresFootText', e.target.value)}
                placeholder="Closing footnote for We Speak Startup"
              />
            </div>
          </div>
        )}

        {/* TAB 6: Who We Work With */}
        {activeTab === 'whoWeWorkWith' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Who We Work With Section</h3>
              <button
                onClick={() => addObjectItem('whoWeWorkWithItems', { title: '', icon: 'Laptop' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Client Type
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.whoWeWorkWithTitle}
                  onChange={(e) => updateField('whoWeWorkWithTitle', e.target.value)}
                  placeholder="Who We Work With"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.whoWeWorkWithSubtitle}
                  onChange={(e) => updateField('whoWeWorkWithSubtitle', e.target.value)}
                  placeholder="We proudly support growth-driven innovators..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {form.whoWeWorkWithItems.map((item, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Client Type #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('whoWeWorkWithItems', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.whoWeWorkWithItems.length - 1}
                        onClick={() => moveObjectItem('whoWeWorkWithItems', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('whoWeWorkWithItems', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-400 block">Label</label>
                    <input
                      className="input-modern py-1"
                      value={item.title}
                      onChange={(e) => updateObjectItem('whoWeWorkWithItems', idx, 'title', e.target.value)}
                      placeholder="e.g. SaaS Companies"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-400 block">Icon Component</label>
                    <select
                      className="input-modern py-1"
                      value={item.icon}
                      onChange={(e) => updateObjectItem('whoWeWorkWithItems', idx, 'icon', e.target.value)}
                    >
                      {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Footnote / CPA Link (HTML allowed)</label>
              <input
                className="input-modern"
                value={form.whoWeWorkWithFootText}
                onChange={(e) => updateField('whoWeWorkWithFootText', e.target.value)}
                placeholder="If you need an accountant for tech startups..."
              />
            </div>
          </div>
        )}

        {/* TAB 7: Why Choose */}
        {activeTab === 'whyChoose' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Why Choose BooksComply Section</h3>
              <button
                onClick={() => addObjectItem('whyChooseItems', { title: '', description: '', icon: 'CheckCircle2' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Reason Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.whyChooseTitle}
                  onChange={(e) => updateField('whyChooseTitle', e.target.value)}
                  placeholder="Why Choose BooksComply"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.whyChooseSubtitle}
                  onChange={(e) => updateField('whyChooseSubtitle', e.target.value)}
                  placeholder="Choosing the right economic partner..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {form.whyChooseItems.map((wc, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Reason Card #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('whyChooseItems', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.whyChooseItems.length - 1}
                        onClick={() => moveObjectItem('whyChooseItems', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('whyChooseItems', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Reason Title</label>
                      <input
                        className="input-modern py-1"
                        value={wc.title}
                        onChange={(e) => updateObjectItem('whyChooseItems', idx, 'title', e.target.value)}
                        placeholder="e.g. GAAP-Compliant"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Icon Component</label>
                      <select
                        className="input-modern py-1"
                        value={wc.icon}
                        onChange={(e) => updateObjectItem('whyChooseItems', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400">Reason Description</label>
                    <input
                      className="input-modern py-1"
                      value={wc.description}
                      onChange={(e) => updateObjectItem('whyChooseItems', idx, 'description', e.target.value)}
                      placeholder="Audit and investor-ready reporting."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Footnote / Expert Link (HTML allowed)</label>
              <input
                className="input-modern"
                value={form.whyChooseFootText}
                onChange={(e) => updateField('whyChooseFootText', e.target.value)}
                placeholder="As startup accounting experts USA..."
              />
            </div>
          </div>
        )}

        {/* TAB 8: Case Study & Results */}
        {activeTab === 'caseStudy' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Case Study &amp; Results Section</h3>
              <button
                onClick={() => addObjectItem('caseStudyItems', { title: '', description: '', icon: 'CheckCircle2' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Results Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.caseStudyTitle}
                  onChange={(e) => updateField('caseStudyTitle', e.target.value)}
                  placeholder="Case Study & Results"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.caseStudySubtitle}
                  onChange={(e) => updateField('caseStudySubtitle', e.target.value)}
                  placeholder="We focus on real results — not just reports. Our impact includes:"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {form.caseStudyItems.map((cs, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Result Card #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('caseStudyItems', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.caseStudyItems.length - 1}
                        onClick={() => moveObjectItem('caseStudyItems', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('caseStudyItems', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Result Metric / Title</label>
                      <input
                        className="input-modern py-1"
                        value={cs.title}
                        onChange={(e) => updateObjectItem('caseStudyItems', idx, 'title', e.target.value)}
                        placeholder="e.g. Investor-Ready"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Icon Component</label>
                      <select
                        className="input-modern py-1"
                        value={cs.icon}
                        onChange={(e) => updateObjectItem('caseStudyItems', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400">Result Details</label>
                    <input
                      className="input-modern py-1"
                      value={cs.description}
                      onChange={(e) => updateObjectItem('caseStudyItems', idx, 'description', e.target.value)}
                      placeholder="Evolving messy books into clean GAAP systems."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Footnote / Conclusion (HTML allowed)</label>
              <input
                className="input-modern"
                value={form.caseStudyFootText}
                onChange={(e) => updateField('caseStudyFootText', e.target.value)}
                placeholder="We don't just record numbers, we improve financial performance."
              />
            </div>
          </div>
        )}

        {/* TAB 9: Pricing */}
        {activeTab === 'pricing' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Pricing &amp; Engagement Section</h3>
              <button
                onClick={() => addObjectItem('pricingItems', { title: '', description: '', icon: 'CreditCard' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add Pricing Model
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pricing Title</label>
                <input
                  className="input-modern"
                  value={form.pricingTitle}
                  onChange={(e) => updateField('pricingTitle', e.target.value)}
                  placeholder="Pricing & Engagement Model"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pricing Subtitle</label>
                <input
                  className="input-modern"
                  value={form.pricingSubtitle}
                  onChange={(e) => updateField('pricingSubtitle', e.target.value)}
                  placeholder="Pricing packages description"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {form.pricingItems.map((item, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-gray-500">Pricing Card #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('pricingItems', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.pricingItems.length - 1}
                        onClick={() => moveObjectItem('pricingItems', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('pricingItems', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Model Name</label>
                      <input
                        className="input-modern py-1"
                        value={item.title}
                        onChange={(e) => updateObjectItem('pricingItems', idx, 'title', e.target.value)}
                        placeholder="e.g. Monthly plans"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-gray-400">Icon Component</label>
                      <select
                        className="input-modern py-1"
                        value={item.icon}
                        onChange={(e) => updateObjectItem('pricingItems', idx, 'icon', e.target.value)}
                      >
                        {COMMON_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400">Model Description</label>
                    <input
                      className="input-modern py-1"
                      value={item.description}
                      onChange={(e) => updateObjectItem('pricingItems', idx, 'description', e.target.value)}
                      placeholder="Consistent, reliable accounting packages."
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 10: FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Frequently Asked Questions</h3>
              <button
                onClick={() => addObjectItem('faqs', { question: '', answer: '' })}
                className="px-3 py-1.5 text-xs text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 font-bold rounded-xl flex items-center gap-1.5"
              >
                <FaPlus /> Add FAQ
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQs Section Title</label>
                <input
                  className="input-modern"
                  value={form.faqsTitle}
                  onChange={(e) => updateField('faqsTitle', e.target.value)}
                  placeholder="Startup Accounting FAQs"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQs Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.faqsSubtitle}
                  onChange={(e) => updateField('faqsSubtitle', e.target.value)}
                  placeholder="Answers to founders' questions..."
                />
              </div>
            </div>

            <div className="space-y-4 mt-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-600 rounded-2xl p-5 space-y-3 bg-gray-50/50 dark:bg-dark-900/40">
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-dark-750 pb-1.5">
                    <span className="font-bold text-xs text-primary-600">FAQ Item #{idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveObjectItem('faqs', idx, -1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === form.faqs.length - 1}
                        onClick={() => moveObjectItem('faqs', idx, 1)}
                        className="p-1 border rounded hover:bg-gray-150 text-xs disabled:opacity-40"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeObjectItem('faqs', idx)}
                        className="p-1 text-red-500 border rounded hover:bg-red-50 text-xs"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Question</label>
                    <input
                      className="input-modern"
                      value={faq.question}
                      onChange={(e) => updateObjectItem('faqs', idx, 'question', e.target.value)}
                      placeholder="e.g. Do you support Delaware C-Corp?"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500">Answer</label>
                    <textarea
                      className="input-modern min-h-[80px]"
                      value={faq.answer}
                      onChange={(e) => updateObjectItem('faqs', idx, 'answer', e.target.value)}
                      placeholder="FAQ answer text..."
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={handleRestoreDefaults}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
        >
          <FaUndo className="w-3.5 h-3.5" /> Revert to Defaults
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-premium flex items-center gap-2"
        >
          <FaSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </PageLayout>
  );
};

export default IndustryPagesCMS;
